import { Injectable, inject } from '@angular/core';
import { Store } from '@ngxs/store';
import { Shift, WithPreparedAttributes } from '@wilson/interfaces';
import {
  RemoveShiftWithoutActivitiesFromUnassignedRegion,
  ShiftTimelineDataState,
  ShiftTimelineDataTransformerService,
  ShiftTimelineUnassignedRegionState,
  TimelineRemoveActivityFromShiftV2,
  TimelineRemoveShiftFromUserV2,
  UpdateShiftWithoutActivitiesFromUnassignedRegion,
} from '@wilson/shift-timeline/state';
import { COMMON_SHIFT_RELATIONS, ShiftsService } from '@wilson/shifts';
import { firstValueFrom } from 'rxjs';
import { TimelineCommonOperationsService } from './timeline-common-operations.service';
@Injectable({
  providedIn: 'root',
})
export class UpdateShiftOnActivityDragService {
  private readonly store = inject(Store);
  private readonly shiftsService = inject(ShiftsService);
  private readonly timelineCommonOperationsService = inject(
    TimelineCommonOperationsService,
  );
  protected shiftTimelineDataTransformerService = inject(
    ShiftTimelineDataTransformerService,
  );

  async updateOrDeleteShiftOnTimeline(
    activityId: string,
    shiftId: string,
    oldUserId: string | null,
  ): Promise<void> {
    if (oldUserId) {
      const activityRows = this.store.selectSnapshot(
        ShiftTimelineDataState.shiftDeterminedActivities(shiftId),
      );
      const allActivities = activityRows.reduce((acc, row) => {
        return { ...acc, ...row };
      }, {});
      delete allActivities[activityId];

      if (!Object.values(allActivities).length) {
        await this.shiftsService.deleteShift(shiftId);
        this.store.dispatch([
          new TimelineRemoveShiftFromUserV2({
            userId: oldUserId,
            shiftId: shiftId,
          }),
          new TimelineRemoveActivityFromShiftV2(activityId, shiftId),
        ]);
      } else {
        this.store.dispatch(
          new TimelineRemoveActivityFromShiftV2(activityId, shiftId),
        );

        this.timelineCommonOperationsService.updateUsersAssignmentsState([
          oldUserId,
        ]);
      }
    }
  }

  async updateOrDeleteShiftOnUnassignedRegion(
    activityId: string,
    shiftId: string,
  ): Promise<void> {
    const activityRows = this.store.selectSnapshot(
      ShiftTimelineUnassignedRegionState.shiftActivitiesRowsOf(shiftId),
    );
    const allActivities = activityRows.reduce((acc, row) => {
      return { ...acc, ...row };
    }, {});
    delete allActivities[activityId];

    if (!Object.values(allActivities).length) {
      await this.shiftsService.deleteShift(shiftId);
      this.store.dispatch([
        new RemoveShiftWithoutActivitiesFromUnassignedRegion({
          shiftId: shiftId,
        }),
      ]);
    } else {
      await this.updateShiftOnUnassignedRegion(shiftId);
    }
  }

  async updateShiftOnUnassignedRegion(shiftId: string): Promise<void> {
    const updatedShift = await firstValueFrom(
      this.shiftsService.get<
        Shift &
          WithPreparedAttributes & {
            id: string;
          }
      >(shiftId, {
        relations: COMMON_SHIFT_RELATIONS,
      }),
    );

    const convertedShift =
      this.shiftTimelineDataTransformerService.convertToUnassignedShift(
        updatedShift,
      );
    this.store.dispatch(
      new UpdateShiftWithoutActivitiesFromUnassignedRegion(convertedShift),
    );
  }
}
